import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// ponytail: leaflet default marker icons need bundler asset config to
// resolve — circleMarker sidesteps that with zero image assets.

export interface MapPoint {
  lat: number;
  lng: number;
  color: string;
  tooltip?: string;
  accuracyM?: number | null;
}

export interface MapCircle {
  lat: number;
  lng: number;
  radiusM: number;
  color: string;
  tooltip?: string;
}

export interface LeafletMapParams {
  points: MapPoint[];
  circles?: MapCircle[];
}

// leafletMap is a Svelte action shared by every inline Leaflet map in the
// app (punch-in's debug map, and the HR employee detail page's per-work-log
// and per-failed-attempt map toggles): points + circles in, a rendered map
// out.
//
// A bare L.map(node) has no view (center/zoom) until setView/fitBounds
// runs, so Map.addLayer() defers each layer's onAdd (and therefore its
// projection) via whenReady() instead of running it synchronously.
// render() below calls .getBounds() on the circles right after adding them,
// which needs that projection immediately — without an initial view here,
// that throws (circle._map still undefined), the exception aborts render()
// before it ever reaches map.fitBounds(), and the map is left permanently
// viewless: zero tiles requested, just Leaflet's default grey background.
// Root cause of the production "grey box" bug — setView must run first.
export function leafletMap(node: HTMLDivElement, params: LeafletMapParams) {
  const first = params.points[0] ?? params.circles?.[0];
  const center: [number, number] = first ? [first.lat, first.lng] : [0, 0];
  const map = L.map(node).setView(center, 15);
  L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
    attribution: '&copy; OpenStreetMap contributors',
    maxZoom: 19
  }).addTo(map);
  const layer = L.layerGroup().addTo(map);

  function render(p: LeafletMapParams) {
    layer.clearLayers();
    const anchors: [number, number][] = [
      ...p.points.map((pt): [number, number] => [pt.lat, pt.lng]),
      ...(p.circles ?? []).map((c): [number, number] => [c.lat, c.lng])
    ];
    if (anchors.length === 0) return;
    const bounds = L.latLngBounds(anchors);

    for (const pt of p.points) {
      const marker = L.circleMarker([pt.lat, pt.lng], {
        radius: 6,
        color: pt.color,
        fillColor: pt.color,
        fillOpacity: 1
      }).addTo(layer);
      if (pt.tooltip) marker.bindTooltip(pt.tooltip);
      if (pt.accuracyM) {
        const acc = L.circle([pt.lat, pt.lng], { radius: pt.accuracyM, color: pt.color, weight: 1, fillOpacity: 0.08 }).addTo(
          layer
        );
        bounds.extend(acc.getBounds());
      }
    }

    for (const c of p.circles ?? []) {
      const marker = L.circleMarker([c.lat, c.lng], { radius: 5, color: c.color, fillColor: c.color, fillOpacity: 1 }).addTo(
        layer
      );
      if (c.tooltip) marker.bindTooltip(c.tooltip);
      const ring = L.circle([c.lat, c.lng], { radius: c.radiusM, color: c.color, weight: 1, fillOpacity: 0.05 }).addTo(layer);
      bounds.extend(ring.getBounds());
    }

    map.fitBounds(bounds, { padding: [24, 24], maxZoom: 17 });
  }

  render(params);
  return {
    update: render,
    // map.stop() cancels any in-flight fitBounds pan/zoom animation before
    // teardown — without it, the animation's next requestAnimationFrame
    // tick can fire after remove(), reading a layer position that no
    // longer exists ("Cannot read properties of undefined
    // (reading '_leaflet_pos')"), an intermittent teardown race the modals
    // (mount/unmount on every open/close) hit far more than a static map.
    destroy: () => {
      map.stop();
      map.remove();
    }
  };
}
